import React from "react";
import {
  CalendarOutlined,
  DollarOutlined,
  HomeOutlined,
  RightOutlined,
  TeamOutlined,
  ToolOutlined,
  UserAddOutlined,
  BankOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { colors, fontSize } from "~/styles/tokens";

const { Title, Paragraph } = Typography;

const OUTCOMES = [
  {
    key: "occupancy",
    icon: <HomeOutlined />,
    labelKey: "JourneyGuide_Welcome_Chip_Occupancy",
    color: colors.actionPrimary,
  },
  {
    key: "rent",
    icon: <DollarOutlined />,
    labelKey: "JourneyGuide_Welcome_Chip_Rent",
    color: colors.statusSuccess,
  },
  {
    key: "renewals",
    icon: <CalendarOutlined />,
    labelKey: "JourneyGuide_Welcome_Chip_Renewals",
    color: colors.statusWarning,
  },
  {
    key: "maintenance",
    icon: <ToolOutlined />,
    labelKey: "JourneyGuide_Welcome_Chip_Maintenance",
    color: colors.actionPrimary,
  },
  {
    key: "access",
    icon: <TeamOutlined />,
    labelKey: "JourneyGuide_Welcome_Chip_Access",
    color: colors.actionTertiary,
  },
  {
    key: "invites",
    icon: <UserAddOutlined />,
    labelKey: "JourneyGuide_Welcome_Chip_Invites",
    color: colors.actionSecondary,
  },
] as const;

interface WelcomeJourneySectionProps {
  isMobile: boolean;
}

/**
 * Welcome — day-at-a-glance outcomes + hierarchy teaser.
 */
export const WelcomeJourneySection: React.FC<WelcomeJourneySectionProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`journey-content${isMobile ? " is-mobile" : ""}`}>
      <Title level={isMobile ? 3 : 2} style={{ marginTop: 0 }}>
        {t("JourneyGuide_Welcome_Title")}
      </Title>
      <Paragraph
        className="journey-lead"
        style={{
          fontSize: isMobile ? fontSize.body : fontSize.emphasis,
        }}
      >
        {t("JourneyGuide_Welcome_Description")}
      </Paragraph>

      <div className="journey-outcome-strip" role="list">
        {OUTCOMES.map((item) => (
          <div
            key={item.key}
            className="journey-outcome-chip"
            role="listitem"
            style={{ ["--chip-color" as string]: item.color }}
          >
            <span className="journey-outcome-chip-icon" aria-hidden>
              {item.icon}
            </span>
            <span>{t(item.labelKey)}</span>
          </div>
        ))}
      </div>

      <div
        className={`journey-hierarchy-teaser${isMobile ? " is-stacked" : ""}`}
        aria-label={t("JourneyGuide_Welcome_Hierarchy_Label")}
      >
        <span className="journey-hierarchy-node">
          <HomeOutlined /> {t("Property")}
        </span>
        <RightOutlined
          className={`journey-hierarchy-arrow${isMobile ? " is-down" : ""}`}
          aria-hidden
          rotate={isMobile ? 90 : 0}
        />
        <span className="journey-hierarchy-node">
          <BankOutlined /> {t("Building")}
        </span>
        <RightOutlined
          className={`journey-hierarchy-arrow${isMobile ? " is-down" : ""}`}
          aria-hidden
          rotate={isMobile ? 90 : 0}
        />
        <span className="journey-hierarchy-node">
          <AppstoreOutlined /> {t("Unit")}
        </span>
      </div>
    </div>
  );
};
